import { NgTemplateOutlet } from '@angular/common';
import { Component, computed, inject, input } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import {
  OnyxDatePipe,
  OnyxLinkComponent,
  OnyxNamePipe,
  OnyxTimePipe,
} from '@onyx/angular';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { ContractorInvoiceCollectiveIssueTermComponent } from '../../../../management-panel/contractors/common/components/contractor-invoice-collective-issue-term/contractor-invoice-collective-issue-term.component';
import { EmployeeHelper } from '../../../../management-panel/employees/common/helpers/employee.helper';
import { OrderStage } from '../../../common/enums/order-stage';
import { OrderStatus } from '../../../common/enums/order-status';
import { OrderModalHelper } from '../../../common/helpers/order-modal.helper';
import { OrderHelper } from '../../../common/helpers/order.helper';
import type { Order, OrderProposal } from '../../../common/interfaces/order';
import { OrderModalStageItemIconComponent } from './order-modal-stage-item-icon/order-modal-stage-item-icon.component';

export enum OrderStageStatus {
  TO_DO = 'to-do',
  IN_PROGRESS = 'in-progress',
  COMPLETED = 'completed',
  SKIPPED = 'skipped',
}

@Component({
  selector: 'app-order-modal-stage-item',
  imports: [
    TranslatePipe,
    OnyxTimePipe,
    OnyxDatePipe,
    OnyxLinkComponent,
    OnyxNamePipe,
    ContractorInvoiceCollectiveIssueTermComponent,
    OrderModalStageItemIconComponent,
    NgTemplateOutlet,
  ],
  templateUrl: './order-modal-stage-item.component.html',
  styleUrl: './order-modal-stage-item.component.scss',
  host: {
    '[class.in-progress]': `stageStatus() === OrderStageStatus.IN_PROGRESS`,
  },
})
export class OrderModalStageItemComponent {
  protected readonly employeeHelper = inject(EmployeeHelper);

  public readonly order = input.required<Order>();
  public readonly stage = input.required<OrderStage>();
  public readonly acceptedProposal = input.required<OrderProposal | null>();
  public readonly hasManualProposal = input.required<boolean>();

  protected readonly I18N = 'orders.orderModal.orderStages';

  protected readonly OrderStage = OrderStage;
  protected readonly OrderStageStatus = OrderStageStatus;
  protected readonly CommonHelper = CommonHelper;

  protected icon = computed(() =>
    OrderModalHelper.getOrderStageIcon(this.stage()),
  );
  protected isFinished = computed(() => OrderHelper.isFinished(this.order()));
  protected isDraft = computed(() => OrderHelper.isDraft(this.order()));
  protected stageStatus = computed(() => {
    const order = this.order();
    const isFinished = this.isFinished();
    const status = order.status.value;

    const isCurrent = (...statuses: OrderStatus[]) => statuses.includes(status);
    const hasManual = this.hasManualProposal();

    const statusesMap: Record<OrderStage, () => OrderStageStatus> = {
      [OrderStage.CREATION]: () =>
        isCurrent(OrderStatus.DRAFT)
          ? OrderStageStatus.IN_PROGRESS
          : OrderStageStatus.COMPLETED,

      [OrderStage.ROUTE_VERIFICATION]: () => {
        if (order.route.isVerified) return OrderStageStatus.COMPLETED;

        return isCurrent(OrderStatus.DRAFT)
          ? OrderStageStatus.TO_DO
          : OrderStageStatus.SKIPPED;
      },

      [OrderStage.VEHICLE_SEARCH]: () => {
        if (isCurrent(OrderStatus.VEHICLE_SEARCH) && !hasManual) {
          return OrderStageStatus.IN_PROGRESS;
        }

        return isCurrent(OrderStatus.DRAFT)
          ? OrderStageStatus.TO_DO
          : OrderStageStatus.COMPLETED;
      },

      [OrderStage.ENGINE_PROPOSAL]: () => {
        if (hasManual) return OrderStageStatus.SKIPPED;
        if (order.assignedData) return OrderStageStatus.COMPLETED;
        if (order.proposalData) return OrderStageStatus.IN_PROGRESS;
        if (isCurrent(OrderStatus.DRAFT, OrderStatus.VEHICLE_SEARCH)) {
          return OrderStageStatus.TO_DO;
        }

        return OrderStageStatus.SKIPPED;
      },

      [OrderStage.MANUAL_PROPOSAL]: () => {
        if (order.assignedData && !hasManual) return OrderStageStatus.SKIPPED;
        if (order.assignedData) return OrderStageStatus.COMPLETED;
        if (hasManual) return OrderStageStatus.IN_PROGRESS;
        if (isCurrent(OrderStatus.DRAFT, OrderStatus.VEHICLE_SEARCH)) {
          return OrderStageStatus.TO_DO;
        }

        return OrderStageStatus.SKIPPED;
      },

      [OrderStage.OUTSOURCING]: () =>
        order.outsourcingData
          ? OrderStageStatus.COMPLETED
          : OrderStageStatus.SKIPPED,

      [OrderStage.REALIZATION]: () => {
        if (isCurrent(OrderStatus.IN_PROGRESS)) {
          return OrderStageStatus.IN_PROGRESS;
        }

        return isCurrent(
          OrderStatus.DRAFT,
          OrderStatus.VEHICLE_SEARCH,
          OrderStatus.TO_ACCEPT,
          OrderStatus.TO_DO,
        )
          ? OrderStageStatus.TO_DO
          : OrderStageStatus.COMPLETED;
      },

      [OrderStage.COMPLETING_DOCUMENTS]: () =>
        isCurrent(OrderStatus.COMPLETING_DOCUMENTS)
          ? OrderStageStatus.IN_PROGRESS
          : isFinished
            ? OrderStageStatus.COMPLETED
            : OrderStageStatus.TO_DO,

      [OrderStage.WAITING_FOR_COLLECTIVE_INVOICE]: () => {
        if (!OrderHelper.hasCollectiveInvoice(order)) {
          return OrderStageStatus.SKIPPED;
        }

        if (isCurrent(OrderStatus.INVOICE_TO_ISSUE)) {
          return OrderStageStatus.IN_PROGRESS;
        }
        if (isFinished && !isCurrent(OrderStatus.COMPLETING_DOCUMENTS)) {
          return OrderStageStatus.COMPLETED;
        }

        return OrderStageStatus.TO_DO;
      },

      [OrderStage.INVOICE_TO_ISSUE]: () =>
        isCurrent(OrderStatus.INVOICE_TO_ISSUE)
          ? OrderStageStatus.IN_PROGRESS
          : isFinished && !isCurrent(OrderStatus.COMPLETING_DOCUMENTS)
            ? OrderStageStatus.COMPLETED
            : OrderStageStatus.TO_DO,

      [OrderStage.PAYMENT_CONFIRMATION]: () => {
        if (
          isCurrent(OrderStatus.WAITING_FOR_PAYMENT, OrderStatus.PARTIALLY_PAID)
        ) {
          return OrderStageStatus.IN_PROGRESS;
        }

        if (
          isFinished &&
          !isCurrent(
            OrderStatus.COMPLETING_DOCUMENTS,
            OrderStatus.INVOICE_TO_ISSUE,
          )
        ) {
          return OrderStageStatus.COMPLETED;
        }

        return OrderStageStatus.TO_DO;
      },
    };

    return statusesMap[this.stage()]?.() ?? OrderStageStatus.SKIPPED;
  });
}
